import React, { FC, useEffect, useState } from 'react';
import {
  useForm,
  Resolver,
  useFieldArray,
  FormProvider
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { SHORT_FIELD_MAX_LENGTH } from '@constants/common';
import { TrashIcon } from '@assets/icons';
import { translate } from '@utils/locale';
import { RhfTextField, RhfDropdown, CustomModal } from '@components';
import { CircularProgress } from '@mui/material';
import FormDualButton from '@components/form-dual-button/FormDualButton';

import {
  CreateProductFormFieldValues,
  ProductSectionProps,
  companyProductFieldType
} from '../types';
import {
  CreateProductFormDefaultFieldValues,
  SectionMapper
} from '../constants';
import RenderScreenshotUpload from '../components/product-screenshot-upload/ProductScreenshot';
import useCreateProduct from '../hooks/useCreateProduct';

const ProductSection: FC<ProductSectionProps> = ({
  editMode,
  updateEditMode,
  disableEdit,
  getResetFunction,
  getResetProductSection,
  companyDetails
}) => {
  const [isCancelClicked, setIsCancelClicked] = useState<boolean>(true);

  const [productEditIndex, setProductEditIndex] = useState<
    number | undefined
  >();
  const [newEditIndex, setNewEditIndex] = useState<number | undefined>();

  const {
    onCreateUpdateProduct,
    isConfirmModalOpen,
    setConfirmModalOpen,
    deletedProductAssetIds,
    setDeletedProductAssetIds,
    onDeleteProduct,
    productDeleteLoading,
    isLoading
  } = useCreateProduct({ updateEditMode });

  const isEditMode = editMode === SectionMapper.product;

  const schema = yup.object({
    products: yup.array().of(
      yup.object().shape({
        productId: yup.string(),
        productName: yup.string().nullable(),
        productStatus: yup.string(),
        productDescription: yup.string().nullable(),
        iosAppLink: yup.string().url('Invalid Url').nullable(),
        webAppLink: yup.string().url('Invalid Url').nullable(),
        androidAppLink: yup.string().url('Invalid Url').nullable()
      })
    )
  });

  const methods = useForm<CreateProductFormFieldValues>({
    resolver: yupResolver(
      schema
    ) as never as Resolver<CreateProductFormFieldValues>,
    defaultValues: CreateProductFormDefaultFieldValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  const {
    control,
    getValues,
    trigger,
    reset,
    formState: { errors },
    clearErrors
  } = methods;

  const { fields } = useFieldArray({
    control,
    name: 'products'
  });

  // const handleAddNewProduct = useCallback(() => {
  //   append({
  //     productId: '',
  //     productName: '',
  //     productStatus: '',
  //     productDescription: '',
  //     iosAppLink: '',
  //     webAppLink: '',
  //     androidAppLink: '',
  //     productScreenshots: [
  //       { id: '', file: undefined, path: '', fileName: '' },
  //       { id: '', file: undefined, path: '', fileName: '' },
  //       { id: '', file: undefined, path: '', fileName: '' },
  //       { id: '', file: undefined, path: '', fileName: '' }
  //     ]
  //   });
  //   onEditSaveButtonClick(fields.length);
  // }, [append]);

  const companyStagProductType = [
    {
      id: 'Live',
      name: 'Live'
    },
    {
      id: 'Stealth',
      name: 'Stealth'
    },
    {
      id: 'Beta',
      name: 'Beta'
    }
  ];

  const showDeleteIcon = (index: number) => {
    return (
      !!getValues(`products.${index}.productId`) &&
      editMode === SectionMapper.product
    );
  };

  const onDiscardDetails = () => {
    const values = getValues();
    reset(getResetFunction(editMode)(values));
    isCancelClicked
      ? updateEditMode?.()
      : updateEditMode?.(SectionMapper.product);
    setProductEditIndex(newEditIndex);
    setNewEditIndex(undefined);
    setConfirmModalOpen(undefined);
  };

  const onUpdateDetails = async () => {
    const productFormValues = getValues();
    productEditIndex !== undefined &&
      onCreateUpdateProduct(
        productFormValues.products[productEditIndex] as companyProductFieldType
      );
  };

  const onEditSaveButtonClick = async (index: number) => {
    if (!editMode) {
      setProductEditIndex(index);
      updateEditMode?.(SectionMapper.product);
      return;
    }
    if (
      editMode !== SectionMapper.product ||
      (editMode === SectionMapper.product && index !== productEditIndex)
    ) {
      setNewEditIndex(index);
      setConfirmModalOpen('discard');
      return;
    }
    const isFormValid = await trigger();
    if (isFormValid) {
      setConfirmModalOpen('save');
    }
  };

  const onClickCancelButton = () => {
    setIsCancelClicked(true);
    setConfirmModalOpen('discard');
  };

  const handleRemoveProduct = async (index: number) => {
    onDeleteProduct(getValues(`products.${index}.productId`));
  };

  useEffect(() => {
    reset(getResetProductSection(getValues()));
  }, [companyDetails]);

  const renderProductFields = () => {
    return (
      <>
        {fields.map((product, index) => (
          <div key={product.id}>
            <div className="flex gap-2 justify-between">
              <div className="flex  flex-row items-center text-xl font-semibold leading-8">
                {translate('company.product.title', { count: '' })}
                {showDeleteIcon(index) && (
                  <>
                    {productDeleteLoading ? (
                      <CircularProgress size="1rem" sx={{ color: 'black' }} />
                    ) : (
                      <TrashIcon
                        className="w-6 h-6 cursor-pointer"
                        onClick={() => handleRemoveProduct(index)}
                      />
                    )}
                  </>
                )}
              </div>
              <FormDualButton
                enableEdit={!disableEdit}
                showCancelOrSaveButton={
                  isEditMode && productEditIndex === index
                }
                onClickCancelButton={onClickCancelButton}
                onClickEditButton={() => onEditSaveButtonClick(index)}
              />
            </div>
            <div className="flex gap-6 pt-6 w-full lg:w-[70%]">
              <div className="w-full">
                <RhfTextField
                  name={`products.${index}.productName`}
                  label={translate('company.product.productName')}
                  placeholder={translate('company.placeholders.productName')}
                  control={control}
                  errors={{
                    [`products.${index}.productName`]:
                      errors.products?.[index]?.productName
                  }}
                  wrapperClass="mb-5 w-full"
                  isDisabled={!(isEditMode && productEditIndex === index)}
                  onClickTextField={() =>
                    clearErrors(`products.${index}.productName`)
                  }
                  showCharacterLimit
                />
                <RhfDropdown
                  name={`products.${index}.productStatus`}
                  control={control}
                  placeholder={translate('company.placeholders.productStatus')}
                  dropdownLOV={companyStagProductType}
                  errors={{
                    [`products.${index}.productStatus`]:
                      errors.products?.[index]?.productStatus
                  }}
                  selectedItem={getValues(`products.${index}.productStatus`)}
                  wrapperClass="mb-5 w-full"
                  isDisabled={!(isEditMode && productEditIndex === index)}
                  description={translate('company.description.productStatus')}
                />
                <RhfTextField
                  name={`products.${index}.productDescription`}
                  label={translate('company.product.productDescription')}
                  placeholder={translate(
                    'company.placeholders.productDescription'
                  )}
                  control={control}
                  multiline={true}
                  errors={errors}
                  rows={3}
                  wrapperClass="mb-5 w-full"
                  maxLength={SHORT_FIELD_MAX_LENGTH}
                  showCharacterLimit
                  description={translate(
                    'company.description.productDescription'
                  )}
                  isDisabled={!(isEditMode && productEditIndex === index)}
                  onClickTextField={() =>
                    clearErrors(`products.${index}.productDescription`)
                  }
                />
              </div>
              <div className="w-full">
                <RhfTextField
                  name={`products.${index}.androidAppLink`}
                  label={translate('company.product.androidAppLink')}
                  placeholder={translate('company.placeholders.androidAppUrl')}
                  control={control}
                  errors={{
                    [`products.${index}.androidAppLink`]:
                      errors.products?.[index]?.androidAppLink
                  }}
                  wrapperClass="mb-5 w-full"
                  isDisabled={!(isEditMode && productEditIndex === index)}
                  onClickTextField={() =>
                    clearErrors(`products.${index}.androidAppLink`)
                  }
                  showCharacterLimit
                />
                <RhfTextField
                  name={`products.${index}.iosAppLink`}
                  label={translate('company.product.iosAppLink')}
                  placeholder={translate('company.placeholders.iosAppUrl')}
                  control={control}
                  errors={{
                    [`products.${index}.iosAppLink`]:
                      errors.products?.[index]?.iosAppLink
                  }}
                  wrapperClass="mb-5 w-full"
                  isDisabled={!(isEditMode && productEditIndex === index)}
                  onClickTextField={() =>
                    clearErrors(`products.${index}.iosAppLink`)
                  }
                  showCharacterLimit
                />

                <RhfTextField
                  name={`products.${index}.webAppLink`}
                  label={translate('company.product.webAppLink')}
                  placeholder={translate('company.placeholders.webAppUrl')}
                  control={control}
                  errors={{
                    [`products.${index}.webAppLink`]:
                      errors.products?.[index]?.webAppLink
                  }}
                  wrapperClass="mb-5 w-full"
                  isDisabled={!(isEditMode && productEditIndex === index)}
                  onClickTextField={() =>
                    clearErrors(`products.${index}.webAppLink`)
                  }
                  showCharacterLimit
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-4 w-full">
              <RenderScreenshotUpload
                isEditMode={isEditMode && productEditIndex === index}
                productIndex={index}
                deletedProductAssetIds={deletedProductAssetIds}
                setDeletedProductAssetIds={setDeletedProductAssetIds}
              />
            </div>
            <p
              className={`mx-[14px] mt-[3px] text-xs leading-5 text-black mb-8 ${
                !isEditMode ? 'text-opacity-40' : 'text-opacity-60'
              }`}>
              {translate('company.description.productScreenshot')}
            </p>
          </div>
        ))}
      </>
    );
  };

  return (
    <div className="pt-9">
      <div className="flex gap-2 justify-between"></div>
      <FormProvider {...methods}>{renderProductFields()}</FormProvider>
      {isConfirmModalOpen && (
        <CustomModal
          isModalOpen={!!isConfirmModalOpen}
          header={
            isConfirmModalOpen === 'discard'
              ? translate('modals.discardPopupTitle')
              : translate('modals.saveInfoTitle')
          }
          primaryButtonLabel={
            isConfirmModalOpen === 'discard'
              ? translate('modals.confirm')
              : translate('update')
          }
          secondaryButtonVariant="text"
          secondaryButtonLabel={translate('modals.cancelButtonTitle')}
          onChangePrimaryButton={
            isConfirmModalOpen === 'discard'
              ? onDiscardDetails
              : onUpdateDetails
          }
          onChangeSecondaryButton={() => setConfirmModalOpen(undefined)}
          isPrimaryButtonLoading={isLoading}
          isPrimaryButtonDisabled={isLoading}
        />
      )}
      {/* <Button
        variant="outlined"
        color="info"
        size="small"
        style={{
          marginTop: 16
        }}
        startIcon={<PlusIcon />}
        disabled={
          editMode !== undefined ||
          (companyDetails?.product_details?.length ?? 0) + 1 === fields.length
        }
        onClick={handleAddNewProduct}>
        {translate('company.product.addProduct')}
      </Button> */}
    </div>
  );
};

export default ProductSection;
